/**
 * Gmail Token Refresh Service
 *
 * Proactively refreshes Gmail access tokens in the background
 * to prevent token expiration errors during sync operations
 */

import { supabase } from './supabaseClient';
import { getGmailAccessToken } from './gmailOAuthService';

// Store refresh intervals
const refreshIntervals = new Map();

/**
 * Start automatic token refresh for all Gmail accounts
 * Refreshes tokens every 50 minutes (before 1 hour expiration)
 * @returns {Promise<void>}
 */
export const startTokenRefreshService = async () => {
  try {

    // Get all Gmail accounts for current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return;
    }


    const { data: accounts, error } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('❌ Error fetching Gmail accounts:', error);
      return;
    }

    if (!accounts || accounts.length === 0) {
      return;
    }

    accounts.forEach((acc, i) => {
    });

    // Start refresh interval for accounts that have a refresh token
    // (accounts saved during signup may not have one yet — they'll get it during onboarding)
    const accountsWithTokens = accounts.filter(a => a.gmail_refresh_token);
    const skippedCount = accounts.length - accountsWithTokens.length;

    if (skippedCount > 0) {
    }

    accountsWithTokens.forEach(account => {
      startRefreshForAccount(account);
    });

  } catch (error) {
    console.error('❌ Error starting token refresh service:', error);
  }
};

/**
 * Start token refresh for a specific account
 * @param {Object} account - Gmail account object
 */
const startRefreshForAccount = (account) => {
  // Clear existing interval if any
  if (refreshIntervals.has(account.id)) {
    clearInterval(refreshIntervals.get(account.id));
  }


  // Refresh immediately on start
  refreshTokenForAccount(account);

  // Then refresh every 50 minutes (3000000ms)
  // Google access tokens expire after 1 hour, so we refresh at 50 mins
  const interval = setInterval(() => {
    refreshTokenForAccount(account);
  }, 50 * 60 * 1000); // 50 minutes

  refreshIntervals.set(account.id, interval);
};

/**
 * Refresh token for a specific account
 * @param {Object} account - Gmail account object
 */
const refreshTokenForAccount = async (account) => {
  // Skip accounts without refresh tokens (saved during signup, pending onboarding)
  if (!account.gmail_refresh_token) {
    return;
  }

  try {

    // Get new access token using refresh token
    const accessToken = await getGmailAccessToken(account.gmail_refresh_token);

    if (accessToken) {

      // Update account status to clear any previous errors
      await supabase
        .from('synced_gmail_accounts')
        .update({
          sync_status: account.sync_status === 'auth_required' ? 'pending' : account.sync_status,
          sync_error: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', account.id);
    }
  } catch (error) {
    console.error(`❌ Failed to refresh token for ${account.gmail_email}:`, error);

    // Check if this is a permanent failure (invalid refresh token)
    const isPermanentError = error.message?.includes('invalid_grant') ||
      error.message?.includes('Token has been expired or revoked');

    if (isPermanentError) {

      // Stop refreshing this account
      stopRefreshForAccount(account.id);

      // Update account status to require re-authentication
      await supabase
        .from('synced_gmail_accounts')
        .update({
          sync_status: 'auth_required',
          sync_error: 'Token expired or revoked. Please re-authenticate.',
          updated_at: new Date().toISOString()
        })
        .eq('id', account.id);
    }
  }
};

/**
 * Stop token refresh for a specific account
 * @param {string} accountId - Gmail account ID
 */
export const stopRefreshForAccount = (accountId) => {
  if (refreshIntervals.has(accountId)) {
    clearInterval(refreshIntervals.get(accountId));
    refreshIntervals.delete(accountId);
  }
};

/**
 * Stop all token refresh intervals
 */
export const stopTokenRefreshService = () => {
  refreshIntervals.forEach((interval, accountId) => {
    clearInterval(interval);
  });
  refreshIntervals.clear();
};

/**
 * Manually trigger token refresh for a specific account
 * Useful after re-authentication
 * @param {string} accountId - Gmail account ID
 */
export const refreshAccountToken = async (accountId) => {
  try {
    const { data: account, error } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('id', accountId)
      .single();

    if (error) throw error;

    await refreshTokenForAccount(account);

    // Restart the interval for this account
    startRefreshForAccount(account);
  } catch (error) {
    console.error('Error manually refreshing account token:', error);
    throw error;
  }
};

/**
 * Add a newly connected Gmail account to the refresh service
 * @param {Object} account - Gmail account object
 */
export const addAccountToRefreshService = (account) => {
  startRefreshForAccount(account);
};

export default {
  startTokenRefreshService,
  stopTokenRefreshService,
  stopRefreshForAccount,
  refreshAccountToken,
  addAccountToRefreshService
};
